import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { financeApi, BankAccountEntry } from '../../api/finance';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Power } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

export function BankAccountsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const bankAccountsQuery = useQuery({ queryKey: ['finance-bank-accounts'], queryFn: () => financeApi.listBankAccounts() });
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts(), enabled: showForm });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-bank-accounts'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createBankAccount({
      accountName: v.accountName, bankName: v.bankName || undefined, accountNumberLast4: v.accountNumberLast4 || undefined,
      glAccountId: Number(v.glAccountId), currencyCode: v.currencyCode || 'NGN',
    }),
    onSuccess: () => { showSuccess('Bank account created.'); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create bank account.'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => financeApi.setBankAccountActive(id, isActive),
    onSuccess: () => { showSuccess('Updated.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <PageHeader title="Bank & Cash Accounts" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Bank Account'}</button>} />
      <p className="page-header-description">Each bank or cash account links to a real, dedicated asset account in the Chart of Accounts - every transaction against it is a genuine GL posting.</p>

      {showForm && (
        <section className="card">
          <h2>New Bank Account</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <div className="form-grid-2col">
              <label>Account Name<input {...register('accountName', { required: true })} placeholder="e.g. Zenith Main Account" /></label>
              <label>Bank Name (optional)<input {...register('bankName')} /></label>
              <label>Account Number - Last 4 Digits (optional)<input {...register('accountNumberLast4')} maxLength={4} /></label>
              <label>Currency<input {...register('currencyCode')} placeholder="NGN" /></label>
              <div className="form-row-full">
                <label>
                  GL Account (must be an asset-type account)
                  <select {...register('glAccountId', { required: true })}>
                    <option value="">- select -</option>
                    {accountsQuery.data?.data.filter((a) => a.account_type === 'asset' && a.posting_allowed).map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
                  </select>
                </label>
              </div>
            </div>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <section className="card">
        {bankAccountsQuery.data && (
          <DataTable<BankAccountEntry>
            rows={bankAccountsQuery.data.data}
            rowKey={(b) => b.id}
            emptyMessage="No bank accounts yet."
            columns={[
              { header: 'Account Name', render: (b) => b.account_name },
              { header: 'Bank', render: (b) => b.bank_name ?? '-' },
              { header: 'Account No.', render: (b) => b.account_number_last4 ? `••••${b.account_number_last4}` : '-' },
              { header: 'GL Account', render: (b) => b.account_code ? `${b.account_code} - ${b.gl_account_name}` : '-' },
              { header: 'Currency', render: (b) => b.currency_code },
              { header: 'Status', render: (b) => <StatusBadge status={b.is_active ? 'active' : 'inactive'} /> },
              { header: '', render: (b) => <IconActionButton icon={Power} label={b.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleActiveMutation.mutate({ id: b.id, isActive: !b.is_active })} /> },
            ]}
          />
        )}
      </section>
    </div>
  );
}
